import { Maybe, maybe } from '@passionware/monads';
import { ExpenseEntity } from '../types';

export type ExpenseSummary = {
  estimate: {
    total: string;
    totalRaw: number;
  };
  actual: Maybe<{
    total: string;
    totalRaw: number;
  }>;
  currency: string;
};

/**
 * Calculate expense summary from EntityValue data
 * This replaces the heavy entity-based calculations with lightweight value-based calculations
 */
export function calculateExpenseSummary(source: ExpenseEntity): ExpenseSummary {
  const { estimateValue, actualValue, currency } = source;

  // Calculate estimate totals
  const estimateRateTotal = calculateRateTotal(estimateValue.rate);
  const estimateCurrency = estimateValue.rate.currency || currency;
  const estimateSummary = {
    total: formatCurrency(estimateRateTotal, estimateCurrency),
    totalRaw: estimateRateTotal
  };

  // Calculate actual totals

  return {
    estimate: estimateSummary,
    actual: maybe.map(actualValue, actualRate => {
      const actualRateTotal = calculateRateTotal(actualRate.rate);
      const actualCurrency = actualRate.rate.currency || currency;
      return {
        total: formatCurrency(actualRateTotal, actualCurrency),
        totalRaw: actualRateTotal
      };
    }),
    currency
  };
}

/**
 * Calculate the total for a rate with optional quantity
 * This replaces the complex calculation logic from v1 helpers
 */
function calculateRateTotal(
  rate: { amount: number; quantity?: number },
  quantity?: number
): number {
  const qty = quantity ?? rate.quantity ?? 1;
  return rate.amount && qty ? rate.amount * qty : 0;
}

/**
 * Format currency value (simplified version of v1 formatCurrency helper)
 */
function formatCurrency(amountScaled100: number, currency: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency || 'USD'
  }).format(amountScaled100 / 100);
}
